"use client";

import { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, signInAnonymously, User as AuthUser } from "firebase/auth";
import { auth } from "@/helpers/firebase/firebase";
import { useAppSelector } from "@/redux/store";
import { User } from "@/models/user";

let guestSignIn: ReturnType<typeof signInAnonymously> | null = null;

export function useApplicantUser() {
  const profile = useAppSelector((state) => state.appReducer.userData);
  const [session, setSession] = useState<AuthUser | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    // The first auth event waits for persisted credentials to be restored.
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!active) return;
      setSession(user);
      if (user) {
        setAuthError(null);
        return;
      }
      // Share anonymous sign-in across StrictMode remounts and both routes.
      if (!guestSignIn) {
        guestSignIn = signInAnonymously(auth).finally(() => { guestSignIn = null; });
      }
      guestSignIn.catch(() => {
        if (active) setAuthError("We couldn't sign you in. Refresh the page to try again.");
      });
    }, () => {
      if (active) setAuthError("We couldn't restore your session. Refresh the page to try again.");
    });
    return () => { active = false; unsubscribe(); };
  }, []);

  const userData = useMemo(() => session ? {
    ...(profile?.uid === session.uid ? profile : {}),
    uid: session.uid,
    email: session.email ?? "",
    isAnonymous: session.isAnonymous,
  } as User & { isAnonymous: boolean } : null, [profile, session]);

  return { userData, authError };
}
